// The full-world quad: terrain, territory and the heatmap are each ONE of
// these (the plan's A.2, A.6 and Part B).
//
// Two triangles cover the whole world, and a caller-supplied fragment
// shader decides every pixel from the per-cell data textures. There is no
// tessellation, no cache texture and no zoom band: the picture is
// resolution-independent by construction.
//
// A SCREEN pass draws the same quad over the whole viewport instead, for the
// background gradient, the vignette and the four arrival veils.
//
// The pass is BACKEND-NEUTRAL: it sets uniforms by name, binds the planes on
// the shared units and asks the pipeline to draw one quad. `worldProgram`
// assembles the `ProgramSource` both backends read.

import type {
  AttribSpec, DataTexture, Gfx, MeshBuffer, Pipeline,
} from './types';
import { CORNER_ATTRIB, CORNER_DECODE_GLSL } from '../gl/corner';
import { UniformValues } from './uniforms';
import type { ProgramSource } from '../shaders/program';
import {
  BACKGROUND_FRAGMENT_WGSL, SCREEN_VERTEX_WGSL, VIGNETTE_FRAGMENT_WGSL, WORLD_VERTEX_WGSL,
} from '../shaders/wgsl/worldquad';
import { wgslModule } from '../shaders/wgsl/module';
import { SHARED_UNIFORMS, type UniformSpec } from '../shaders/prelude';
import type { CameraUniforms } from '../layer';
import { packHex, rgb } from '../../render/color';
import {
  WATER_DEEP_LOW, WATER_DEEP_MID, WATER_DEEP_TOP, VIGNETTE_ALPHA, VIGNETTE_COLOR,
  VIGNETTE_INNER, VIGNETTE_OUTER,
} from '../../gen/presentation';

/**
 * The sampler units. Every world fragment shader reads the same names on
 * the same units, so a pass never states a unit of its own.
 */
export const SAMPLER_UNITS: Readonly<Record<string, number>> = {
  uOwners: 0,
  uBlocked: 1,
  uHeat: 2,
  uDist: 3,
  uCompR: 4,
  // 5 to 8 are free: they carried the field border's planes, which left
  // the tree with that border (R532).
  // 9 is the survey of the viewer's faction (R2626).
  uSurvey: 9,
  // 10 is the depth band of each cell (R2713).
  uDepth: 10,
  // 11 is the speed field of the current looks, one texel a flow-grid
  // node (R2827).
  uSpeed: 11,
};

/** The per-cell planes a world pass may read. Any of them may be absent. */
export interface WorldTextures {
  owners?: DataTexture | null;
  blocked?: DataTexture | null;
  heat?: DataTexture | null;
  dist?: DataTexture | null;
  compR?: DataTexture | null;
  survey?: DataTexture | null;
  depth?: DataTexture | null;
  speed?: DataTexture | null;
}

/** Everything a world pass is told about the world, once a frame. */
export interface WorldEnv {
  cols: number;
  rows: number;
  hexSize: number;
  colPitch: number;
  /** The playable rectangle in world px. */
  worldW: number;
  worldH: number;
  /** How far past the rectangle the quad reaches, in world px. */
  pad: number;
  /** 8 rgba entries, 32 floats: [0] unowned, [1..5] factions, [6] accent, [7] neutral. */
  palette: Float32Array;
  /** Seconds since the loop started. */
  timeS: number;
  textures: WorldTextures;
}

/**
 * The three uniforms the DRAW owns and no pass list names.
 *
 * They stand in front of every world block, because WebGPU packs the block
 * into one buffer at byte offsets and the order is the contract. `uCols` and
 * `uRows` were here too until 2026-09-11; no fragment ever read them.
 */
export const WORLD_DRIVER_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uViewProj', type: 'mat3', value: [1, 0, 0, 0, 1, 0, 0, 0, 1], dynamic: true },
  { name: 'uRes', type: 'vec2', value: [1, 1], dynamic: true },
  { name: 'uPad', type: 'float', value: 0, dynamic: true },
];

/**
 * The prelude every world fragment shader is given.
 *
 * A caller writes functions and `main` only: no `#version`, no precision
 * statement, no uniform declaration from this list. An unused uniform is
 * optimised out and its setter then finds no location and returns, so a
 * one-line shader pays for nothing it ignores.
 *
 * Both precision statements are required. World coordinates reach 5320 px,
 * and Apple GPUs run a real 16-bit `mediump` (the plan's A.9).
 */
export const WORLD_PRELUDE = `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;
precision highp usampler2D;
in vec2 vWorld;
in vec2 vUV;
uniform usampler2D uOwners;
uniform usampler2D uBlocked;
uniform sampler2D uHeat;
uniform sampler2D uDist;
uniform sampler2D uCompR;
uniform vec4 uPalette[8];
uniform ivec2 uGrid;
uniform float uHexSize;
uniform float uColPitch;
uniform float uPad;
uniform vec2 uWorld;
uniform vec2 uRes;
uniform mat3 uViewProj;
uniform mat3 uViewInv;
uniform float uTime;
uniform float uZoom;
uniform float uDpr;
uniform float uPxPerWorld;
out vec4 o;
`;

/**
 * The world-space vertex shader.
 *
 * The quad spans `[-uPad, WORLD + COL_PITCH/2 + uPad]` by
 * `[-uPad, WORLD_H + uPad]` (Part B's naming reconciliation): odd rows are
 * shifted right by half a column pitch, so their hexes reach past the
 * playable width, and the pad lets a border stroke stand outside the world
 * edge the way the traced reference does.
 */
export const WORLD_VERTEX = `#version 300 es
layout(location=0) in float aCorner;
uniform mat3 uViewProj;
uniform vec2 uWorld;
uniform float uColPitch;
uniform float uPad;
out vec2 vWorld;
out vec2 vUV;
void main() {
${CORNER_DECODE_GLSL}
  vec2 lo = vec2(-uPad, -uPad);
  vec2 hi = vec2(uWorld.x + uColPitch * 0.5 + uPad, uWorld.y + uPad);
  vWorld = mix(lo, hi, corner);
  vUV = corner;
  gl_Position = vec4((uViewProj * vec3(vWorld, 1.0)).xy, 0.0, 1.0);
}`;

/**
 * The screen-space vertex shader.
 *
 * `vUV` is (0,0) at the top left, which is the orientation the canvas
 * gradients of `render/water.ts` are drawn in. `vWorld` still holds the
 * world point under the pixel, because `uViewInv` maps device px to world
 * px and the corners are exactly the corners of the drawing surface.
 */
export const SCREEN_VERTEX = `#version 300 es
layout(location=0) in float aCorner;
uniform mat3 uViewInv;
uniform vec2 uRes;
out vec2 vWorld;
out vec2 vUV;
void main() {
${CORNER_DECODE_GLSL}
  vec2 dev = corner * uRes;
  vWorld = (uViewInv * vec3(dev, 1.0)).xy;
  vUV = vec2(corner.x, 1.0 - corner.y);
  gl_Position = vec4(corner * 2.0 - 1.0, 0.0, 1.0);
}`;

/** What `worldProgram` is told about one pass. */
export interface WorldProgramOpts {
  name: string;
  /** Which shared vertex source to use. `raw` vertex overrules it. */
  mode?: 'world' | 'screen';
  /**
   * A vertex source of the pass's own, in place of the world or screen one.
   * The two outline passes draw a mesh and carry one.
   */
  vertex?: string;
  /** The fragment source. */
  fragment: string;
  /**
   * Is the fragment source COMPLETE?
   *
   * A raw source carries its own `#version` line, its own precision
   * statements and its own declaration of the A.6 contract, so
   * `WORLD_PRELUDE` must not be prepended to it. The three map passes of
   * `../shaders/` are built this way and share a prelude of their own.
   */
  raw?: boolean;
  /** The pass's OWN uniforms, after the driver's and the shared ones. */
  uniforms?: readonly UniformSpec[];
  /** The attributes. The default is the corner alone. */
  attribs?: readonly AttribSpec[];
  /**
   * The WGSL twin's BODY: functions, module consts and `@fragment fn fs`.
   *
   * The twin writes the body ALONE. The header, the vertex input struct and
   * the vertex entry are assembled here, from the block this function put
   * together, so a twin can never declare a uniform the packer does not
   * write and can never reach a device without a vertex entry. A pass with
   * no twin yet leaves it out, and the WebGPU pipeline factory then throws
   * and names the program.
   */
  wgslBody?: string;
  /**
   * A vertex ENTRY of the pass's own, in place of the shared world or screen
   * one. The two outline passes carry one; its input struct is generated
   * from `attribs` like every other.
   */
  wgslVertex?: string;
  /** The varying struct, where the pass does not read the shared one. */
  wgslVaryings?: string;
}

/**
 * One world or screen pass, as both backends read it.
 *
 * It is the ONE place the uniform block is assembled, so the driver's three
 * uniforms and the shared contract stand in the same order in every pass.
 * A RAW source states the shared half in its own list already, so the
 * helper adds it only for a prelude-built source.
 */
export function worldProgram(o: WorldProgramOpts): ProgramSource {
  const uniforms = [
    ...WORLD_DRIVER_UNIFORMS,
    ...(o.raw ? [] : SHARED_UNIFORMS),
    ...(o.uniforms ?? []),
  ];
  const attribs = o.attribs ?? [CORNER_ATTRIB];
  return {
    name: o.name,
    glsl: {
      vertex: o.vertex ?? (o.mode === 'screen' ? SCREEN_VERTEX : WORLD_VERTEX),
      fragment: o.raw ? o.fragment : WORLD_PRELUDE + o.fragment,
    },
    // The ONE assembly rule (`../shaders/wgsl/module.ts`): the header comes
    // from the block above, so the shader's struct and the packer's offsets
    // are one list; the vertex entry is the shared one unless the pass owns
    // one, so no world module reaches a pipeline without a vertex stage.
    wgsl: o.wgslBody === undefined ? '' : wgslModule({
      uniforms,
      attribs,
      body: o.wgslBody,
      vertex: o.wgslVertex ?? (o.mode === 'screen' ? SCREEN_VERTEX_WGSL : WORLD_VERTEX_WGSL),
      varyings: o.wgslVaryings,
    }),
    uniforms,
    attribs,
  };
}

export class WorldPass {
  private extra: ((p: Pipeline) => void) | null = null;

  constructor(
    private readonly gfx: Gfx,
    readonly pipeline: Pipeline,
    readonly name: string,
  ) {}

  /**
   * Registers the pass's own uniforms. It runs after the shared contract on
   * every draw, so a pass sets its style figures without a second draw path.
   */
  setExtra(fn: ((p: Pipeline) => void) | null): void { this.extra = fn; }

  /**
   * Sets the contract, binds the samplers and draws the two triangles.
   *
   * A `mesh` draws in place of the quad: the same uniform contract and the
   * same fragment shader, but the geometry comes from an indexed triangle
   * mesh. The mesh counts its own draw call.
   */
  draw(cam: CameraUniforms, env: WorldEnv, mesh?: MeshBuffer): number {
    const p = this.pipeline;
    if (!p.use()) return 0;
    p.uniformMatrix3fv('uViewProj', cam.viewProj);
    p.uniformMatrix3fv('uViewInv', cam.viewInv);
    p.uniform1f('uZoom', cam.zoom);
    p.uniform1f('uDpr', cam.dpr);
    p.uniform1f('uPxPerWorld', cam.pxPerWorld);
    p.uniform2f('uRes', cam.w * cam.dpr, cam.h * cam.dpr);
    p.uniform2i('uGrid', env.cols, env.rows);
    p.uniform1f('uHexSize', env.hexSize);
    p.uniform1f('uColPitch', env.colPitch);
    p.uniform1f('uPad', env.pad);
    p.uniform2f('uWorld', env.worldW, env.worldH);
    p.uniform1f('uTime', env.timeS);
    p.uniform4fv('uPalette', env.palette);
    this.bind('uOwners', env.textures.owners);
    this.bind('uBlocked', env.textures.blocked);
    this.bind('uHeat', env.textures.heat);
    this.bind('uDist', env.textures.dist);
    this.bind('uCompR', env.textures.compR);
    this.bind('uSurvey', env.textures.survey);
    this.bind('uDepth', env.textures.depth);
    this.bind('uSpeed', env.textures.speed);
    this.extra?.(p);
    let n = 1;
    if (mesh) n = mesh.draw();
    else p.drawQuad();
    this.gfx.check(this.name);
    return n;
  }

  private bind(name: string, tex: DataTexture | null | undefined): void {
    if (!tex || !this.pipeline.has(name)) return;
    const unit = SAMPLER_UNITS[name];
    tex.bind(unit);
    this.pipeline.sampler(name, unit);
  }

  dispose(): void { this.pipeline.dispose(); }
}

// ---------------------------------------------------------------------------
// The two demonstration passes (the plan's A.5 rows 1 and 16)
// ---------------------------------------------------------------------------

/** `#rrggbb` to three floats in 0..1. */
export function cssRgb(hex: string): [number, number, number] {
  return rgb(packHex(hex));
}

/**
 * The deep-water gradient behind everything (`render/water.ts:32-34`): three
 * stops down the viewport, opaque, and so the frame never needs a clear
 * colour of its own.
 */
export const BACKGROUND_FRAGMENT = `
uniform vec3 uTopColor;
uniform vec3 uMidColor;
uniform vec3 uLowColor;
void main() {
  float t = vUV.y;
  vec3 c = t < 0.5
    ? mix(uTopColor, uMidColor, t * 2.0)
    : mix(uMidColor, uLowColor, (t - 0.5) * 2.0);
  o = vec4(c, 1.0);
}`;

/** The three stops, as the data layer states them. */
export const BACKGROUND_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uTopColor', type: 'vec3', value: cssRgb(WATER_DEEP_TOP) },
  { name: 'uMidColor', type: 'vec3', value: cssRgb(WATER_DEEP_MID) },
  { name: 'uLowColor', type: 'vec3', value: cssRgb(WATER_DEEP_LOW) },
];

export function backgroundProgram(): ProgramSource {
  return worldProgram({
    name: 'background', mode: 'screen',
    fragment: BACKGROUND_FRAGMENT, uniforms: BACKGROUND_UNIFORMS,
    wgslBody: BACKGROUND_FRAGMENT_WGSL,
  });
}

/** Builds a pass whose own uniforms keep the values its list states. */
function createFixedPass(
  make: (src: ProgramSource) => WorldPass, src: ProgramSource, uniforms: readonly UniformSpec[],
): WorldPass {
  const pass = make(src);
  const values = new UniformValues(uniforms);
  pass.setExtra((p) => values.apply(p));
  return pass;
}

/** Builds the background pass through the engine's own pass factory. */
export function createBackgroundPass(make: (src: ProgramSource) => WorldPass): WorldPass {
  return createFixedPass(make, backgroundProgram(), BACKGROUND_UNIFORMS);
}

/**
 * The corner darkening (`[presentation.water]`, ui-map-interface.md §7).
 *
 * The reference stretched a 512² radial gradient over the window, so the
 * falloff is measured in half-viewport units on each axis: clear at the
 * inner radius and the colour at the alpha from the outer one.
 */
const VIGNETTE_RGB = rgb(VIGNETTE_COLOR);

export const VIGNETTE_FRAGMENT = `
uniform vec3 uVignetteColor;
uniform float uInner;
uniform float uOuter;
uniform float uVignetteAlpha;
void main() {
  float d = length((vUV - 0.5) * 2.0);
  float t = clamp((d - uInner) / max(uOuter - uInner, 1e-5), 0.0, 1.0);
  float a = t * uVignetteAlpha;
  o = vec4(uVignetteColor * a, a);
}`;

/** The four figures above, as the spec list every backend reads. */
export const VIGNETTE_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uVignetteColor', type: 'vec3', value: VIGNETTE_RGB },
  { name: 'uInner', type: 'float', value: VIGNETTE_INNER },
  { name: 'uOuter', type: 'float', value: VIGNETTE_OUTER },
  { name: 'uVignetteAlpha', type: 'float', value: VIGNETTE_ALPHA },
];

export function vignetteProgram(): ProgramSource {
  return worldProgram({
    name: 'vignette', mode: 'screen',
    fragment: VIGNETTE_FRAGMENT, uniforms: VIGNETTE_UNIFORMS,
    wgslBody: VIGNETTE_FRAGMENT_WGSL,
  });
}

/** Builds the vignette pass through the engine's own pass factory. */
export function createVignettePass(make: (src: ProgramSource) => WorldPass): WorldPass {
  return createFixedPass(make, vignetteProgram(), VIGNETTE_UNIFORMS);
}
